import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Calculator, Users, Scale, Rocket, ChevronRight, LayoutGrid, Handshake } from 'lucide-react';
import { useContact } from '../../context/ContactContext';
import SpotlightCard from '../ui/SpotlightCard';
import AnimatedBadge from '../ui/AnimatedBadge';

export default function CoreServicesSection() {
  const { openModal } = useContact();
  const services = [
    {
      icon: Calculator,
      title: "Contabilidade & Fiscal",
      desc: "Escrituração automatizada via sistema Domínio, garantindo precisão absoluta e conformidade total.",
      items: ["Demonstrações Financeiras", "Gestão de Ativos", "Auditoria SPED"]
    },
    {
      icon: Users,
      title: "Departamento Pessoal",
      desc: "Gestão completa da folha de pagamento e eSocial, focando na segurança jurídica das suas relações.",
      items: ["Folha Mensal", "Gestão de Benefícios", "Rescisões & Férias"]
    },
    {
      icon: Rocket,
      title: "Abertura de Empresas",
      desc: "Tiramos seu projeto do papel com planejamento societário e registro express em todos os órgãos.",
      items: ["Registro JUCESP", "CNPJ Express", "Consultoria Pré-Abertura"]
    },
    {
      icon: Scale,
      title: "Regularização & Burocracia",
      desc: "Sua empresa está inapta ou com problemas contábeis? Resolvemos notificações da RFB e pendências.",
      items: ["Alvarás & Licenças", "Certidões Negativas", "Apoio a Notificações"]
    },
    {
      icon: LayoutGrid,
      title: "Informação Centralizada",
      desc: "Portal do Cliente Onvio: documentos em PDF, calendário de obrigações e atendimento exclusivo digital.",
      items: ["Acesso 24/7", "Protocolos Digitais", "Backup de Documentos"]
    },
    {
      icon: Handshake,
      title: "Benefícios & Parcerias",
      desc: "Ecossistema de parcerias em Seguros, Certificados e RH para trazer agilidade ao seu negócio.",
      items: ["Certificação Digital", "Seguros Corporativos", "Consultoria de RH"]
    }
  ];

  return (
    <section id="contabil" className="w-full bg-background py-24 relative overflow-hidden">
      <div id="pessoal" className="absolute top-0"></div>
      <div id="legal" className="absolute top-1/2"></div>
      <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24 px-4 md:px-0">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-4 md:mb-6"
          >
            <AnimatedBadge>
              Foque no que importa, a burocracia é conosco.
            </AnimatedBadge>
          </motion.div>
          <h2 className="text-2xl md:text-5xl font-black text-foreground tracking-tight mb-6 md:mb-8 leading-tight">
            Soluções para quem <br className="hidden md:block" /> quer <span className="text-orange-500">Voar Alto.</span>
          </h2>
          <p className="text-sm md:text-xl text-slate-400 font-medium">
            Atendemos de MEI a S/A de Capital Fechado, Indústria, Comércio e Serviços. 
            Preço justo e atendimento singular.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              className="h-full"
            >
              <SpotlightCard className="h-full" innerClassName="p-8 md:p-12" radius="2.5rem md:3rem">
                <div className="flex flex-col h-full">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-foreground/5 rounded-2xl flex items-center justify-center mb-8 md:mb-10 group-hover:bg-orange-600 transition-colors duration-500 mx-auto md:mx-0">
                    <service.icon className="w-6 h-6 md:w-8 md:h-8 text-orange-500 group-hover:text-white transition-colors" />
                  </div>
                  
                  <h3 className="text-xl md:text-2xl font-extrabold text-foreground mb-4 md:mb-6 tracking-tight text-center md:text-left">{service.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-8 md:mb-10 flex-1 text-center md:text-left">
                    {service.desc}
                  </p>

                  <ul className="space-y-4 mb-10 md:mb-12 flex flex-col items-center md:items-start mx-auto md:mx-0">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-[9px] md:text-[10px] font-black text-slate-500 group-hover:text-slate-300 transition-colors uppercase tracking-widest">
                        <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-orange-500/40 group-hover:bg-orange-500 transition-colors shadow-[0_0_10px_rgba(249,115,22,0.3)]"></div>
                        {item}
                      </li>
                    ))}
                  </ul>

                  <div className="flex justify-center md:justify-start">
                    <a 
                      href="https://wa.me/5511949830659"
                      
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 text-orange-500 font-black text-[10px] uppercase tracking-[0.2em] group/btn mt-auto"
                    >
                      Consultar Especialista
                      <ChevronRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </SpotlightCard>
            </motion.div>
          ))}
        </div>

        {/* Integration Note */}
        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-16 md:mt-20 text-center text-slate-600 text-[10px] md:text-xs font-bold uppercase tracking-widest px-4"
        >
          Trabalhamos com os melhores Softwares de Gestão do Mercado em um Ecossistema Integrado.
        </motion.p>
      </div>
    </section>
  );
}
